import ReactEcharts from 'echarts-for-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ChartControlsProps,
  SortConfiguration,
} from '../../redux/ChartPoperties/ChartControlsInterface';
import { ChartDataFieldProps } from './ChartsCommonInterfaces';
import EmptyQuery from './EmptyQuery';
import { RootState } from '../../redux';
import { tooltipStyle } from '../Common/EChartTooltip';
import {
  checkUidPresenceInCheckedMeasureForTooltip,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import { messages } from '../CommonFunctions/aliases';
import { toast } from 'react-toastify';
import { LEGEND_THRESHOLDS } from '../ChartOptions/ChartThresholds';
const palette = ['#FF6384', '#36A2EB', '#FFCE56'];

interface CalendarChartProps {
  propKey: string;
  graphDimension?: any;
  chartArea?: any;
  graphTileSize?: number;
  colorScheme?: string;
  softUI?: boolean;
  fontFamily?: string;
}

const CalendarChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  colorScheme,
  fontFamily,
  softUI,
}: CalendarChartProps) => {
  const {
    chartControls,
    chartProperties,
    chartType,
    tooltipFormats,
    chartAxes,
    isTooltipEnabled,
    showDash,
  } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
    chartType: state.chartProperties?.properties[propKey]?.chartType,
    tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
    chartAxes: state.chartProperties.properties[propKey]?.chartAxes,
    isTooltipEnabled:
      state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
    showDash: state.tabTileProps.showDash,
  }));

  // TODO: showing alert with the message of "only can drop data tatatype columns" even its a data type column
  var yearsArray: string[] | number[] | any = [];
  var uniqueYears: string[] | number[] | any = [];

  var chartControl: ChartControlsProps = chartControls.properties[propKey];

  //////////// Code commented will be required in future /////////////////
  // const { tabState, selectedTab } = useSelector((state: RootState) => ({
  //   tabState: state.tabState,
  //   selectedTab: state.tabTileProps.selectedTabId,
  // }));

  // const chartRef = useRef<any>(null);
  // const scrollRef = useRef<HTMLDivElement>(null);
  // const dispatch = useDispatch();

  // // const scrollRef = useRef<HTMLDivElement>(null);
  // const savedScrollTop = useRef(0);

  // console.log(propKey, graphDimension, colorScheme, softUI, chartArea, graphTileSize);

  // useEffect(() => {
  //   console.log('modified');
  // }, [chartControls, chartProperties]);

  // // 🔁 Run BEFORE render to save scroll
  // // useLayoutEffect(() => {
  // //   const container = scrollRef.current;
  // //   console.log(container, scrollRef, container?.scrollTop);
  // //   if (container) {
  // //     savedScrollTop.current = container.scrollTop;
  // //   }
  // // }, [tabState.tabs[selectedTab].hoveredItems]);

  // useEffect(() => {
  //   const container = scrollRef.current;
  //   if (container) {
  //     container.scrollTop = savedScrollTop.current;
  //   }
  // }, [tabState.tabs[selectedTab].hoveredItems]);
  // // useEffect(() => {
  // //   const container = scrollRef.current;
  // //   console.log(container, scrollRef);
  // //   if (!container) return;

  // //   console.log(savedScrollTop);

  // //   const timeout = setTimeout(() => {
  // //     container.scrollTop = savedScrollTop.current;
  // //   }, 0); // Delay to ensure layout is done

  // //   return () => clearTimeout(timeout);
  // //   // }, [savedScrollTop.current]);
  // // }, [tabState.tabs[selectedTab].hoveredItems]);

  var chartControl: ChartControlsProps = chartControls.properties[propKey];

  const scrollRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<any>(null);

  const chartData: any[] = useMemo(
    () => (chartControl.chartData ? chartControl.chartData : []),
    [chartControl.chartData],
  );
  let validChartData = chartData;

  const [calendarArray, setCalendarArray] = useState<any[]>([]);
  const [seriesArray, setSeriesArray] = useState<any[]>([]);

  const [maxValue, setMaxValue] = useState<number>(0);
  const [minValue, setMinValue] = useState<number>(0);

  const [chartDataKeys, setChartDataKeys] = useState<string[]>([]);

  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (
      !chartControl.colorScheme ||
      (chartArea === 'dashboard' && !chartControl.colorScale.manualColor)
    ) {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const selectedTheme = chartThemes.length > 0 ? chartThemes[0] : allThemes[0];

  const uniqueYearsCount = useMemo(() => {
    if (chartData.length === 0 || !chartProperties.properties[propKey]?.chartAxes[1]?.fields[0]) {
      return 0;
    }

    const objKey = chartProperties.properties[propKey].chartAxes[1].fields[0].displayname;
    const yearsSet = new Set<string>();

    chartData.forEach((el: any) => {
      const date = new Date(el[objKey]);
      if (!isNaN(date.getTime())) {
        yearsSet.add(String(date.getFullYear()));
      }
    });

    if (yearsSet.size > 10 && !showDash) {
      toast.warning(`${messages.chart.excessiveCalendarChartData}`, {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: {
          top: '10px',
        },
      });
    }

    return yearsSet.size;
  }, [chartData, chartProperties.properties[propKey].chartAxes, propKey]);

  const totalCalendarHeight = useMemo(() => {
    if (uniqueYearsCount === 0) return 60;
    return (
      uniqueYearsCount *
        (chartControl.calendarStyleOptions.calendarGap +
          chartControl.calendarStyleOptions.calendarHeight * 7) +
      60
    );
  }, [uniqueYearsCount, chartControl.calendarStyleOptions]);

  const containerHeight = useMemo(() => {
    if (chartArea === 'dashboard' || graphTileSize) {
      return graphDimension.height;
    }
    return Math.max(graphDimension.height, totalCalendarHeight);
  }, [graphDimension.height, totalCalendarHeight, chartArea, graphTileSize]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length >= 1) {
      if (typeof chartData === 'object' && chartData.length > 0) {
        setChartDataKeys(Object.keys(chartData[0]));
      }
    }
  }, [chartData]);

  useEffect(() => {
    if (chartData.length >= 1) {
      if (chartProperties.properties[propKey].chartAxes[1].fields.length > 0) {
        let objKey = chartProperties.properties[propKey].chartAxes[1].fields[0].displayname;

        validChartData = chartData.filter((el: any) => {
          const date = new Date(el[objKey]);
          const isValid = !isNaN(date.getTime());
          return isValid;
        });

        var measureField: ChartDataFieldProps =
          chartProperties.properties[propKey].chartAxes[2].fields[0];
        if (measureField) {
          var maxFieldName: string = '';
          maxFieldName = measureField.displayname;
          // if ("timeGrain" in measureField) {
          // 	maxFieldName = `${measureField.timeGrain} of ${measureField.fieldname}`;
          // } else {
          // 	maxFieldName = `${measureField.agg} of ${measureField.fieldname}`;
          // }

          var max: number = 0;
          var min: number = 100000000;
          validChartData.forEach((element: any) => {
            if (element[maxFieldName] > max) {
              max = element[maxFieldName];
            }
            if (element[maxFieldName] < min) {
              min = element[maxFieldName];
            }
          });

          setMaxValue(max);
          setMinValue(min);
        }

        // getting years of dates
        validChartData.forEach((el: any) => {
          const timestampformate = new Date(el[objKey]);
          const year: number = timestampformate.getFullYear();
          yearsArray.push(JSON.stringify(year));
        });

        // getting unique values
        uniqueYears = [...new Set(yearsArray)];
        const mostRecentToLeastRecentYearList = [...uniqueYears];
        //get most recent to least recent year list
        mostRecentToLeastRecentYearList.sort((a: string, b: string) => Number(b) - Number(a));
        let sortedYearList = mostRecentToLeastRecentYearList.slice(0, 10);

        const dimensionField = chartProperties.properties[propKey].chartAxes[1].fields[0];
        const sortConfigurations = chartControl.sortConfigurations || [];
        const sortConfig = sortConfigurations.find(
          (config: SortConfiguration) => config.fieldId === dimensionField?.uId,
        );
        //sort if sort order is ascending
        if (sortConfig && sortConfig.sortOrder) {
          if (sortConfig.sortOrder === 'Ascending') {
            sortedYearList.sort((a: string, b: string) => Number(a) - Number(b));
          }
        }
        // setting props for each value
        const calendarArrayValues = sortedYearList.map((yr: string | number, i: number) => {
          return {
            top:
              i * (chartControl.calendarStyleOptions.calendarHeight * 7) +
              30 +
              i * chartControl.calendarStyleOptions.calendarGap,
            left: chartControl.chartMargin.left + 35,
            right: chartControl.chartMargin.right,
            // height: (graphDimension.height * 80) / 100 / uniqueYears.length,
            range: yr,
            cellSize: ['auto', chartControl.calendarStyleOptions.calendarHeight],
            splitLine: {
              show: chartControl.calendarStyleOptions.showSplitLine,
              lineStyle: {
                color: chartControl.calendarStyleOptions.splitLineColor,
                width: chartControl.calendarStyleOptions.splitLineWidth,
                type: chartControl.calendarStyleOptions.splitLineType,
              },
            },
            yearLabel: {
              show: chartControl.calendarStyleOptions.showYearLabel,
              margin: chartControl.calendarStyleOptions.yearLabelMargin,
              color: chartControl.calendarStyleOptions.yearLabelColor
                ? chartControl.calendarStyleOptions.yearLabelColor
                : selectedTheme?.dark
                  ? '#ffffff'
                  : '#757474',
              fontSize: chartControl.calendarStyleOptions.yearLabelFontSize,
              fontFamily: fontFamily,
              position: chartControl.calendarStyleOptions.yearLabelPosition,
              backgroundColor: chartControl.calendarStyleOptions.yearLabelBackground
                ? chartControl.calendarStyleOptions.yearLabelBackgroundColor
                : 'transparent',
              padding: chartControl.calendarStyleOptions.yearLabelBackground ? [2, 4] : 0,
              borderRadius: chartControl.calendarStyleOptions.yearLabelBackground ? 4 : 0,
            },
            dayLabel: {
              show: chartControl.calendarStyleOptions.showDayLabel,
              margin: chartControl.calendarStyleOptions.dayLabelMargin,
              color: chartControl.calendarStyleOptions.dayLabelColor
                ? chartControl.calendarStyleOptions.dayLabelColor
                : selectedTheme?.dark
                  ? '#ffffff'
                  : '#757474',
              fontSize: chartControl.calendarStyleOptions.dayLabelFontSize,
              fontFamily: fontFamily,
              position: chartControl.calendarStyleOptions.dayLabelPosition,
              backgroundColor: chartControl.calendarStyleOptions.dayLabelBackground
                ? chartControl.calendarStyleOptions.dayLabelBackgroundColor
                : 'transparent',
              padding: chartControl.calendarStyleOptions.dayLabelBackground ? [2, 4] : 0,
              borderRadius: chartControl.calendarStyleOptions.dayLabelBackground ? 4 : 0,
            },
            monthLabel: {
              show: chartControl.calendarStyleOptions.showMonthLabel,
              margin: chartControl.calendarStyleOptions.monthLabelMargin,
              color: chartControl.calendarStyleOptions.monthLabelColor
                ? chartControl.calendarStyleOptions.monthLabelColor
                : selectedTheme?.dark
                  ? '#ffffff'
                  : '#757474',
              fontSize: chartControl.calendarStyleOptions.monthLabelFontSize,
              fontFamily: fontFamily,
              position: chartControl.calendarStyleOptions.monthLabelPosition,
              backgroundColor: chartControl.calendarStyleOptions.monthLabelBackground
                ? chartControl.calendarStyleOptions.monthLabelBackgroundColor
                : 'transparent',
              padding: chartControl.calendarStyleOptions.monthLabelBackground ? [2, 4] : 0,
              borderRadius: chartControl.calendarStyleOptions.monthLabelBackground ? 4 : 0,
            },
          };
        });

        setCalendarArray(calendarArrayValues);

        // setting individual year props and data

        const seriesArrayValues = sortedYearList.map((yr: string | number, index: number) => {
          return {
            type: 'heatmap',
            coordinateSystem: 'calendar',
            calendarIndex: index,
            emphasis: {
              focus: 'self',
            },
            blur: {
              itemStyle: {
                opacity: 0.5,
              },
            },
            data: getVirtulData(yr),
            itemStyle: softUI
              ? {
                  shadowColor: 'rgba(0, 0, 0, 0.5)', // Shadow color
                  shadowBlur: 10, // Blur effect for shadow
                  shadowOffsetX: 3, // Horizontal offset of shadow
                  shadowOffsetY: 3, // Vertical offset of shadow
                }
              : {},
          };
        });
        setSeriesArray(seriesArrayValues);

        // //ind chart height
        // inChartHeight = (graphDimension.height * 80) / 100 / uniqueYears.length;
        // //chart gap
      }
    }
  }, [chartControl, chartControl.chartData, selectedTheme]);

  function getVirtulData(year: string | number) {
    let objKey = chartProperties.properties[propKey].chartAxes[1].fields[0].displayname;
    //let objKey = chartProperties.properties[propKey].chartAxes[1].fields[0].displayname;
    var virtualData: any[] = [];

    // getting measure value as day value for individual year
    chartData.forEach((el: any) => {
      var elYear = new Date(el[objKey]).getFullYear();
      if (year === JSON.stringify(elYear)) {
        virtualData.push(Object.values(el));
      }
    });

    return virtualData;
  }

  useLayoutEffect(() => {
    const chartInstance = chartRef.current?.getEchartsInstance?.();
    if (chartInstance && !chartInstance.isDisposed?.()) {
      const timer = setTimeout(() => {
        chartInstance.resize();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [graphDimension.height, graphDimension.width, containerHeight]);

  // const [hoveredColumns, setHoveredColumns] = useState<Record<string, any>[]>([]);

  // useEffect(() => {
  //   const timeout = setTimeout(() => {
  //     if (
  //       tabState.tabs[selectedTab].hoveredPropkey &&
  //       tabState.tabs[selectedTab].hoveredPropkey === propKey
  //     ) {
  //       return;
  //     }
  //     const chartInstance = chartRef.current?.getEchartsInstance?.();
  //     if (!chartInstance || chartInstance.isDisposed?.()) return;

  //     for (let i = 0; i < seriesArray.length; i++) {
  //       const series = seriesArray[i];

  //       const dataIndex = series.data.findIndex((item: [string, number]) =>
  //         tabState.tabs[selectedTab].hoveredItems?.some(
  //           (hovered: Record<string, any>) => String(item[0]) === String(hovered[chartDataKeys[0]]),
  //         ),
  //       );

  //       if (dataIndex !== -1) {
  //         chartInstance.dispatchAction({
  //           type: 'highlight',
  //           seriesIndex: i,
  //           dataIndex,
  //         });

  //         break;
  //       }
  //     }
  //   }, 30); // slight delay helps when charts update

  //   return () => clearTimeout(timeout);
  // }, [tabState.tabs[selectedTab].hoveredItems, chartData]);

  // const hoveredRef = useRef<Record<string, any> | null>(null);
  // const hoveredColumnsRef = useRef<Record<string, any>[]>([]);

  // const handleChartReady = (chartInstance: any) => {
  //   chartInstance.on('mouseover', (params: any) => {
  //     const key = chartDataKeys[0];
  //     const value = params.data?.[0];
  //     if (value == null) return;

  //     const isDuplicate = hoveredColumnsRef.current.some((item) => item[key] === value);

  //     if (!isDuplicate) {
  //       const container = scrollRef.current;
  //       console.log(container, scrollRef, container?.scrollTop);
  //       if (container) {
  //         savedScrollTop.current = container.scrollTop;
  //       }
  //       hoveredColumnsRef.current.push({ [key]: value });

  //       requestAnimationFrame(() => {
  //         dispatch(updateHoveredItems(selectedTab, hoveredColumnsRef.current, propKey));
  //       });
  //       // setTimeout(() => {
  //       //   dispatch(updateHoveredItems(selectedTab, hoveredColumnsRef.current, propKey));
  //       // }, 0);
  //       // dispatch(updateHoveredItems(selectedTab, hoveredColumnsRef.current, propKey));
  //     }
  //   });

  //   chartInstance.on('mouseout', () => {
  //     hoveredColumnsRef.current = [];
  //     dispatch(updateHoveredItems(selectedTab, [], propKey));
  //   });

  //   // chartRef.current = chartInstance;

  //   // chartInstance.on('mouseover', (params: any) => {
  //   //   if (params?.data && hoveredRef.current?.[chartDataKeys[0]] !== params.data[0]) {
  //   //     const newEntry = { [chartDataKeys[0]]: params.data[0] };
  //   //     hoveredRef.current = newEntry;

  //   //     setHoveredColumns((prev) => {
  //   //       const isDuplicate = prev.some((item) => item[chartDataKeys[0]] === params.data[0]);
  //   //       if (!isDuplicate) {
  //   //         return [...prev, newEntry];
  //   //       }
  //   //       return prev;
  //   //     });
  //   //   }
  //   // });

  //   // chartInstance.on('mouseout', (params: any) => {
  //   //   if (params?.data) {
  //   //     hoveredRef.current = null;
  //   //     setHoveredColumns([]);
  //   //   }
  //   // });
  // };

  // useEffect(() => {
  //   dispatch(updateHoveredItems(selectedTab, hoveredColumns, propKey));
  // }, [hoveredColumns]);

  // useEffect(() => {
  //   const container = scrollRef.current;
  //   if (!container) return;
  //   const scrollTop = container.scrollTop;

  //   const timeout = setTimeout(() => {
  //     container.scrollTop = scrollTop;
  //   }, 0);

  //   return () => clearTimeout(timeout);
  // }, [chartOption]); // only re-run if chartOption actually changes

  const tooltipFormatter = useCallback(
    (params: any) => {
      const allKeys = Object.keys(chartData[0]);
      const dimensionKey = chartAxes[1]?.fields[0]?.displayname;
      const dimensionValue = params.value[0];
      const measureKey = allKeys.find((key) => key !== dimensionKey);
      const measureValue = params.value[1];
      let tempData: { [key: string]: string | number } = {};
      tempData[dimensionKey] = dimensionValue;
      tempData[measureKey as string] = measureValue;
      //-----remove a unchecked measure--------//
      const _data: { [key: string]: string | number } = {};
      Object.keys(tempData).forEach((key: string) => {
        const resp = checkUidPresenceInCheckedMeasureForTooltip(
          chartAxes,
          key,
          tooltipFormats?.checkedMeasure,
        );
        if (resp) {
          _data[key] = tempData[key];
        }
      });
      let mergedData = { ..._data };
      const extraResponses = tooltipFormats.extraMeasureFieldsResponses;
      if (tooltipFormats && extraResponses.length > 0 && dimensionKey) {
        const dimensionValue = tempData[dimensionKey];
        //-------find extra fields--------//
        const extraFieldObj = extraResponses.find(
          (obj: any) => String(obj[dimensionKey]) === String(dimensionValue),
        );
        //------remove dimension key from extra fields object------//
        const measureKey = chartAxes.find((axis: any) => axis.name === 'Measure').fields[0]
          .displayname;
        if (extraFieldObj) {
          const { [dimensionKey]: _, ...restExtraFields } = extraFieldObj;
          //-----Remove unchecked measure field after adding extra measure ----//
          let filteredFields = { ...mergedData, ...restExtraFields };
          const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
            chartAxes,
            measureKey,
            tooltipFormats?.checkedMeasure,
          );
          if (!isPresent) {
            delete filteredFields[measureKey];
          }
          //---------send final response in tooltip data i.e checked measure plus extra fields-------//
          mergedData = { ...filteredFields };
        }
      }
      return tooltipStyle(
        params.color,
        mergedData,
        tooltipFormats,
        chartAxes,
        dimensionKey,
        undefined,
        undefined,
        fontFamily,
      );
    },
    [chartData, chartAxes, tooltipFormats],
  );

  const chartOption = useMemo(() => {
    return {
      color: selectedTheme?.colors || [],
      backgroundColor: selectedTheme?.background || [],
      animation: false,
      legend: {},
      tooltip: {
        show: isTooltipEnabled,
        confine: true,
        trigger: 'item',
        backgroundColor: 'white',
        borderWidth: 1,
        padding: 0,
        textStyle: {
          color: '#333',
          fontSize: 16,
        },
        appendToBody: true,
        extraCssText:
          'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word; max-width: 300px;',
        formatter: (params: any) => tooltipFormatter(params),
      },
      dataset: {
        source: chartData,
      },
      visualMap: {
        type: chartControl.calendarStyleOptions.pieceWise ? 'piecewise' : 'continuous',
        show:
          Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.calendar.h
            ? chartData.length >= 1
              ? chartControl.legendOptions?.showLegend
              : false
            : false,
        itemHeight: chartControl.calendarStyleOptions.pieceWise
          ? chartControl.calendarStyleOptions?.height
          : chartControl.calendarStyleOptions?.width,
        itemWidth: chartControl.calendarStyleOptions.pieceWise
          ? chartControl.calendarStyleOptions?.width
          : chartControl.calendarStyleOptions?.height,
        itemGap: chartControl.legendOptions?.itemGap,
        left: chartControl.legendOptions?.position?.left,
        top: chartControl.legendOptions?.position?.top,
        orient: chartControl.calendarStyleOptions?.orientation,
        min:
          chartControl.colorScale.colorScaleType === 'Manual'
            ? chartControl.colorScale.min !== parseInt('')
              ? Number(chartControl.colorScale.min)
              : 0
            : minValue,
        max:
          chartControl.colorScale.colorScaleType === 'Manual'
            ? chartControl.colorScale.max !== parseInt('')
              ? Number(chartControl.colorScale.max)
              : 0
            : maxValue,
        inRange: {
          color: chartControl.colorScale.manualColor
            ? [chartControl.colorScale.minColor, chartControl.colorScale.maxColor]
            : (selectedTheme?.colors?.slice(0, 2) ?? palette),
        },
      },
      calendar: calendarArray,
      series: seriesArray,
    };
  }, [
    chartControl,
    chartData,
    minValue,
    maxValue,
    selectedTheme,
    isTooltipEnabled,
    tooltipFormatter,
    calendarArray,
    seriesArray,
    graphDimension.height,
  ]);

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <div
      ref={scrollRef}
      style={{
        padding: '1rem',
        width: graphDimension.width,
        height: containerHeight,
        overflow: 'auto',
        margin: 'auto',
        border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
      }}
    >
      <ReactEcharts
        ref={chartRef}
        option={chartOption}
        notMerge={true}
        lazyUpdate={true}
        style={{
          width: '100%',
          height: `${totalCalendarHeight}px`,
        }}
      />
    </div>
  );
};

export default CalendarChart;
